import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IBeacon } from "../../../shared/interfaces/beacon/IBeacon";
import DeleteModal from "../../../shared/components/DeleteModal";
import { BeaconService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";
import PageSizeSelect from "../../../shared/components/PageSizeSelect";
import Pagination from "../../../shared/components/Pagination";
import { IPagination } from "../../../shared/interfaces/shared/IPagination";

interface IProps {
    beacons: IBeacon[];
    pagination: IPagination;
    totalPages: number;
    handleUpdateList: () => void;
    changeFilterHandler: (
        name: string,
        value: string | number | boolean | number[] | null,
    ) => void;
}

const BeaconList = ({
    beacons,
    pagination,
    totalPages,
    handleUpdateList,
    changeFilterHandler,
}: IProps) => {
    const { t } = useTranslation();

    const [isDeleteVisible, setIsDeleteVisible] = useState(false);
    const [selectedBeacon, setSelectedBeacon] = useState<IBeacon | undefined>(
        undefined,
    );

    const handleCloseDelete = () => {
        setIsDeleteVisible(false);
        setSelectedBeacon(undefined);
    };

    const handleDeleteBeacon = (id: number) => {
        BeaconService.delete(id)
            .then((response) => {
                if (response !== undefined || response === undefined) {
                    enqueueSnackbar(
                        t("toasts.successfullyDeleted", {
                            name: selectedBeacon?.title,
                        }),
                        {
                            variant: "success",
                        },
                    );
                    handleCloseDelete();
                    handleUpdateList();
                }
            })
            .catch((error) => {
                handleError(error);
                handleCloseDelete();
            });
    };

    return (
        <React.Fragment>
            <div className="table-responsive">
                <table className="table table-hover" id="pc-dt-simple">
                    <thead>
                        <tr>
                            <th>{t("beacon.title")}</th>
                            <th>{t("beacon.uuid")}</th>
                            <th>{t("beacon.minor")}</th>
                            <th>{t("beacon.major")}</th>
                            <th>{t("general.institution")}</th>
                            <th>{t("general.action")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {beacons.map((beacon) => {
                            return (
                                <tr key={beacon?.id}>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div
                                                    className="avtar avtar-s bg-light-primary"
                                                    style={{ width: "40px", height: "40px" }}
                                                >
                                                    <i className="ph-duotone ph-broadcast f-20"></i>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <h6 className="mb-0">{beacon?.title}</h6>
                                                <small className="text-muted">
                                                    {beacon?.description?.substring(0, 50)}
                                                    {beacon?.description && beacon.description.length > 50 ? "..." : ""}
                                                </small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <code className="text-primary">{beacon?.uuid}</code>
                                    </td>
                                    <td>{beacon?.minor}</td>
                                    <td>{beacon?.major}</td>
                                    <td>{beacon?.institutionName || "-"}</td>
                                    <td>
                                        <Link
                                            to={`/beacons/${beacon?.id}`}
                                            className="avtar avtar-xs btn-link-secondary"
                                        >
                                            <i className="ti ti-edit f-20"></i>
                                        </Link>
                                        <Link
                                            to="#"
                                            onClick={() => {
                                                setSelectedBeacon(beacon);
                                                setIsDeleteVisible(true);
                                            }}
                                            className="avtar avtar-xs btn-link-secondary"
                                        >
                                            <i className="ti ti-trash f-20"></i>
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <PageSizeSelect
                value={pagination.size}
                onChange={(size) => changeFilterHandler("size", size)}
            />

            <Pagination
                currentPage={pagination.page}
                totalPages={totalPages}
                onPageChange={(page) => changeFilterHandler("page", page)}
            />

            <DeleteModal
                show={isDeleteVisible}
                handleClose={handleCloseDelete}
                handleDeleteId={() => handleDeleteBeacon(Number(selectedBeacon?.id))}
                name={selectedBeacon?.title}
            />
        </React.Fragment>
    );
};

export default BeaconList;
